import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  BookOpen,
  FileText,
  Building2,
  Building,
  Users,
  Settings,
  LogOut,
  Receipt,
  Wallet,
  Bot,
  ChevronDown,
  FilePlus,
  FileCheck,
  Package,
  UserCircle,
  BarChart3,
  Plus,
  UserPlus,
  X,
  AlertTriangle,
  Globe,
  Shield,
  RefreshCw,
  Mail,
  Clock,
  Link2,
  Truck,
  Landmark,
  Flag,
  Warehouse,
  Briefcase,
  Calculator,
  Layers,
  Smartphone,
  Moon,
  Sun
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { useTheme } from "@/components/ThemeToggle";

// Language configuration with flags (using emoji flags that work across platforms)
export const languages = [
  { code: 'sv', name: 'Svenska', flag: '🇸🇪', flagAlt: 'SE' },
  { code: 'no', name: 'Norsk', flag: '🇳🇴', flagAlt: 'NO' },
  { code: 'da', name: 'Dansk', flag: '🇩🇰', flagAlt: 'DK' },
  { code: 'en', name: 'English', flag: '🇬🇧', flagAlt: 'GB' },
  { code: 'es', name: 'Español', flag: '🇪🇸', flagAlt: 'ES' },
];

// Flag component with fallback
const FlagIcon = ({ flag, flagAlt, size = "text-lg" }) => (
  <span className={`${size} leading-none`} role="img" aria-label={flagAlt}>
    {flag}
  </span>
);

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, switchCompany, token } = useAuth();
  const { t, i18n } = useTranslation();
  const { resolvedTheme, setTheme } = useTheme();
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    return localStorage.getItem('yubi_language') || i18n.language || 'sv';
  });
  const [companies, setCompanies] = useState([]);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [showInviteMember, setShowInviteMember] = useState(false);
  const [companyForm, setCompanyForm] = useState({ name: "", org_number: "" });
  const [inviteEmail, setInviteEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const toggleTheme = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();
    if (!companyForm.name) {
      toast.error("Ange företagsnamn");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/companies`, companyForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(prev => [...prev, response.data]);
      toast.success("Företag skapat!");
      setShowAddCompany(false);
      setCompanyForm({ name: "", org_number: "" });
      // Switch to new company
      switchCompany(response.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte skapa företag");
    } finally {
      setLoading(false);
    }
  };

  const handleInviteMember = async (e) => {
    e.preventDefault();
    if (!inviteEmail) {
      toast.error("Ange e-postadress");
      return;
    }
    const companyId = user?.active_company_id;
    if (!companyId) {
      toast.error("Inget aktivt företag valt");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/companies/${companyId}/invite`, 
        { email: inviteEmail, role: "viewer" },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(response.data.message);
      setShowInviteMember(false);
      setInviteEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte bjuda in medlem");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navSections = [
    {
      title: t('nav.overview'),
      items: [
        { path: "/", icon: LayoutDashboard, label: t('nav.dashboard') },
      ]
    },
    {
      title: t('nav.customersSales') || 'Kunder & Försäljning',
      items: [
        { path: "/sales-invoices", icon: FilePlus, label: t('nav.invoices') },
        { path: "/quotes", icon: FileCheck, label: t('nav.quotes') },
        { path: "/abonnemang", icon: RefreshCw, label: t('nav.subscriptions') || 'Abonnemang' },
        { path: "/customers", icon: UserCircle, label: t('nav.customers') },
        { path: "/articles", icon: Package, label: t('nav.articles') },
      ]
    },
    {
      title: t('nav.suppliersPurchases') || 'Leverantörer & Inköp',
      items: [
        { path: "/suppliers", icon: Truck, label: t('nav.suppliers') || 'Leverantörer' },
        { path: "/supplier-invoices", icon: Receipt, label: t('nav.supplierInvoices') || 'Leverantörsfakturor' },
        { path: "/fixed-assets", icon: Landmark, label: t('nav.fixedAssets') || 'Anläggningar' },
        { path: "/inventory", icon: Warehouse, label: t('nav.inventory') || 'Lager' },
      ]
    },
    {
      title: t('nav.bookkeeping'),
      items: [
        { path: "/accounts", icon: BookOpen, label: t('nav.chartOfAccounts') },
        { path: "/bookkeeping", icon: FileText, label: t('nav.journalEntries') },
        { path: "/invoices", icon: Receipt, label: t('nav.invoiceScanner') },
        { path: "/email-scanner", icon: Mail, label: t('nav.emailScanner') || 'E-postskanner' },
        { path: "/bank", icon: Wallet, label: t('nav.bankImport') },
        { path: "/cost-centers", icon: Layers, label: t('nav.costCenters') || 'Kostnadsställen' },
      ]
    },
    {
      title: t('nav.personnelReports'),
      items: [
        { path: "/payroll", icon: Users, label: t('nav.payroll') },
        { path: "/reports", icon: BarChart3, label: t('nav.reports') },
        { path: "/budgeting", icon: Calculator, label: t('nav.budgeting') || 'Budgetering' },
        { path: "/projects", icon: Briefcase, label: t('nav.projects') || 'Projekt' },
        { path: "/inkasso", icon: AlertTriangle, label: t('nav.inkasso') },
      ]
    },
    {
      title: t('nav.tools'),
      items: [
        { path: "/assistant", icon: Bot, label: t('nav.aiAssistant') },
        { path: "/swedish-integrations", icon: Flag, label: t('nav.swedishIntegrations') || 'Svenska Integrationer' },
        { path: "/fortnox", icon: Link2, label: t('nav.fortnox') || 'Fortnox' },
        { path: "/bankid", icon: Smartphone, label: 'BankID & Medlemskap' },
        { path: "/scheduler", icon: Clock, label: t('nav.scheduler') || 'Schemaläggare', adminOnly: true },
        { path: "/team", icon: Shield, label: t('nav.team'), adminOnly: true },
        { path: "/settings", icon: Settings, label: t('nav.settings') },
      ]
    }
  ];

  // Filter out admin-only items if user is not admin
  const filteredNavSections = navSections.map(section => ({
    ...section,
    items: section.items.filter(item => !item.adminOnly || user?.role === 'Administrator')
  }));

  const activeCompany = companies.find(c => c.id === user?.active_company_id);

  return (
    <aside className="sidebar fixed left-0 top-0 h-screen w-[280px] flex flex-col z-50" data-testid="sidebar">
      {/* Logo */}
      <div className="px-4 pt-0.5 pb-0.5">
        <Link to="/" className="block" data-testid="logo-link">
          <img 
            src="/yubi-logo.png" 
            alt="YUBI AI" 
            className="w-full h-auto object-contain"
          />
        </Link>
      </div>

      {/* Company Switcher */}
      {companies.length > 0 && (
        <div className="px-4 pb-2 border-b border-white/10">
          <DropdownMenu>
            <DropdownMenuTrigger className="w-full" data-testid="company-switcher">
              <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors cursor-pointer">
                <div className="flex items-center gap-3">
                  <Building2 size={18} className="text-slate-400" />
                  <div className="text-left">
                    <p className="text-sm font-medium truncate max-w-[150px]">
                      {activeCompany?.name || "Välj företag"}
                    </p>
                    {activeCompany?.org_number && (
                      <p className="text-xs text-slate-400">{activeCompany.org_number}</p>
                    )}
                  </div>
                </div>
                <ChevronDown size={16} className="text-slate-400" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[248px]">
              {companies.map((company) => (
                <DropdownMenuItem
                  key={company.id}
                  onClick={() => switchCompany(company.id)}
                  className="cursor-pointer"
                  data-testid={`company-option-${company.id}`}
                >
                  <div className="flex items-center gap-2">
                    {company.id === user?.active_company_id && (
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                    )}
                    <span>{company.name}</span>
                  </div>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowAddCompany(true)} className="cursor-pointer text-indigo-400">
                <Plus size={16} className="mr-2" />
                {t('company.addCompany')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowInviteMember(true)} className="cursor-pointer text-indigo-400">
                <UserPlus size={16} className="mr-2" />
                {t('company.inviteMember')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 overflow-y-auto">
        {filteredNavSections.map((section, idx) => (
          <div key={idx} className="mb-4">
            <p className="text-xs uppercase text-slate-500 font-medium px-4 mb-2">{section.title}</p>
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`sidebar-link flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium ${
                        isActive ? "active" : ""
                      }`}
                      data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
                    >
                      <Icon size={18} />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center">
            <span className="text-white font-medium">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
        </div>
        
        {/* Language Selector */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="w-full flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-white/10 transition-colors mb-1"
              data-testid="language-selector"
            >
              <Globe size={16} />
              <span className="flex-1 text-left">
                {languages.find(l => l.code === currentLanguage)?.name || 'Swedish'}
              </span>
              <FlagIcon 
                flag={languages.find(l => l.code === currentLanguage)?.flag || '🇸🇪'} 
                flagAlt={languages.find(l => l.code === currentLanguage)?.flagAlt || 'SE'}
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            {languages.map((lang) => (
              <DropdownMenuItem
                key={lang.code}
                onClick={() => {
                  setCurrentLanguage(lang.code);
                  localStorage.setItem('yubi_language', lang.code);
                  i18n.changeLanguage(lang.code);
                  toast.success(`${t('language.changed')} ${lang.name}`);
                }}
                className={`flex items-center justify-between cursor-pointer ${
                  currentLanguage === lang.code ? 'bg-indigo-50' : ''
                }`}
                data-testid={`lang-${lang.code}`}
              >
                <span className="flex items-center gap-2">
                  <span>{lang.name}</span>
                  <FlagIcon flag={lang.flag} flagAlt={lang.flagAlt} />
                </span>
                {currentLanguage === lang.code && (
                  <span className="text-indigo-600 text-xs font-medium">✓</span>
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        
        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="w-full flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-white/10 transition-colors mb-1"
          data-testid="theme-toggle"
          title={resolvedTheme === 'dark' ? 'Byt till ljust läge' : 'Byt till mörkt läge'}
        >
          {resolvedTheme === 'dark' ? (
            <>
              <Sun size={16} className="text-yellow-400" />
              <span className="flex-1 text-left">Ljust läge</span>
            </>
          ) : (
            <>
              <Moon size={16} />
              <span className="flex-1 text-left">Mörkt läge</span>
            </>
          )}
        </button>
        
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
          data-testid="logout-btn"
        >
          <LogOut size={16} />
          <span>{t('nav.logout')}</span>
        </button>
      </div>

      {/* Add Company Dialog */}
      <Dialog open={showAddCompany} onOpenChange={setShowAddCompany}>
        <DialogContent size="default">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 size={20} />
              {t('company.addCompany')}
            </DialogTitle>
            <DialogDescription>
              {t('company.createCompanyDescription')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddCompany} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="company-name">{t('company.companyNameRequired')}</Label>
              <Input
                id="company-name"
                value={companyForm.name}
                onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                placeholder={t('company.companyNamePlaceholder')}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company-org">{t('company.orgNumber')}</Label>
              <Input
                id="company-org"
                value={companyForm.org_number}
                onChange={(e) => setCompanyForm({ ...companyForm, org_number: e.target.value })}
                placeholder={t('company.orgNumberPlaceholder')}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddCompany(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={loading}>
                {loading ? t('company.creating') : t('company.createCompanyBtn')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invite Member Dialog */}
      <Dialog open={showInviteMember} onOpenChange={setShowInviteMember}>
        <DialogContent size="default">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus size={20} />
              {t('company.inviteTeamMember')}
            </DialogTitle>
            <DialogDescription>
              {t('company.inviteToCompany')} {activeCompany?.name || t('company.yourCompany')}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInviteMember} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">{t('company.inviteEmailRequired')}</Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={t('company.inviteEmailPlaceholder')}
                required
              />
              <p className="text-xs text-slate-500">
                {t('company.inviteDescription')}
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowInviteMember(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={loading}>
                {loading ? t('company.sending') : t('company.invite')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </aside>
  );
};

export default Sidebar;
